import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { noop } from 'lodash-es';
import { bootRouteChunkNames, DEFAULT_HOME_CONTENT_CHUNK, HOME_SHELL_CHUNK, ROUTE_CHUNK_ENTRIES, routeChunkNameFor } from '../../shared/route-chunk-map';
const ROUTE_IDLE_POLICY = {
    idleTimeoutMs: 2000,
    fallbackDelayMs: 200
};
const MEDIA_IDLE_POLICY = {
    idleTimeoutMs: null,
    fallbackDelayMs: 2000
};
const WEB_RETRY_DELAYS_MS = [
    500,
    1500
];
const NATIVE_RETRY_DELAYS_MS = [];
const CHUNK_LOAD_TELEMETRY_TYPE = 'chunk_load';
const STALE_BUNDLE_RELOAD_MARKER = 'anchor.staleBundleReloaded';
const STALE_BUNDLE_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;
const STALE_BUNDLE_MESSAGES = [
    /loading chunk .* failed/i,
    /failed to fetch dynamically imported module/i
];
const loaderErrorFields = (error)=>error !== null && error !== void 0 ? error : {};
const timedOut = (error)=>loaderErrorFields(error).type === 'timeout';
const failureKindOf = (report)=>{
    if (report.outcome === 'recovered') return null;
    const { type } = loaderErrorFields(report.error);
    return typeof type === 'string' ? type : 'unknown';
};
const chunkUrlOf = (error)=>{
    const { request } = loaderErrorFields(error);
    return typeof request === 'string' ? request : null;
};
const buildChunkLoadTelemetry = (report, pageStatus, automated, path)=>({
        type: CHUNK_LOAD_TELEMETRY_TYPE,
        page_name: CHUNK_LOAD_TELEMETRY_TYPE,
        hierarchy: [],
        outcome: report.outcome,
        chunk_id: String(report.chunkId),
        attempts: report.attempts,
        failure_kind: failureKindOf(report),
        chunk_url: chunkUrlOf(report.error),
        automated,
        visibility_state: pageStatus.visibilityState,
        online: pageStatus.online,
        ms_since_page_load: Math.round(pageStatus.msSincePageLoad),
        path
    });
const hasStaleBundleMessage = (message)=>typeof message === 'string' && STALE_BUNDLE_MESSAGES.some((pattern)=>pattern.test(message));
const isStaleBundleError = (error)=>{
    const candidate = error;
    return (candidate === null || candidate === void 0 ? void 0 : candidate.name) === 'ChunkLoadError' || hasStaleBundleMessage(candidate === null || candidate === void 0 ? void 0 : candidate.message);
};
const reloadedRecently = (marker, nowMs)=>nowMs - Number(marker) < STALE_BUNDLE_RELOAD_COOLDOWN_MS;
export class ChunkLifecycle {
    static install(ports) {
        const chunks = new ChunkLifecycle(ports);
        ports.interceptChunkLoads(chunks.retryingInterceptor());
        return chunks;
    }
    isRouteReady(path) {
        const chunkName = routeChunkNameFor(path);
        return chunkName == null || this.readyRoutes.has(chunkName);
    }
    markRouteRendered(path) {
        const chunkName = routeChunkNameFor(path);
        if (chunkName != null) {
            this.readyRoutes.add(chunkName);
        }
    }
    preloadBootRoutes(path) {
        const bootChunkNames = bootRouteChunkNames(path);
        const chunkNames = bootChunkNames.length > 0 ? bootChunkNames : [
            HOME_SHELL_CHUNK,
            DEFAULT_HOME_CONTENT_CHUNK
        ];
        Promise.all(chunkNames.map((chunkName)=>this.loadRoute(chunkName))).catch(noop);
    }
    navigateWhenRouteReady(path, param) {
        let { navigate, startWaiting, stopWaiting } = param;
        const chunkName = routeChunkNameFor(path);
        if (chunkName == null || this.readyRoutes.has(chunkName)) {
            navigate();
            return;
        }
        startWaiting();
        const stopWaitingAndNavigate = ()=>{
            stopWaiting();
            navigate();
        };
        void this.loadRoute(chunkName).then(stopWaitingAndNavigate, stopWaitingAndNavigate);
    }
    warmWhenIdle(set) {
        return this.whenPrefetchAllowed(set === 'routes' ? ()=>this.warmRoutesOnePerIdleSlot() : ()=>this.warmMedia());
    }
    recover(error) {
        const { getNow, sessionStorage, browser } = this.ports;
        const nowMs = getNow().getTime();
        const alreadyReloaded = reloadedRecently(sessionStorage.getItem(STALE_BUNDLE_RELOAD_MARKER), nowMs);
        if (!isStaleBundleError(error)) return 'not-stale';
        if (alreadyReloaded) return 'stale-not-reloaded';
        sessionStorage.setItem(STALE_BUNDLE_RELOAD_MARKER, String(nowMs));
        browser.locationReload();
        return 'reloading';
    }
    retryingInterceptor() {
        const { isNative, scheduler } = this.ports;
        const retryDelaysMs = isNative ? NATIVE_RETRY_DELAYS_MS : WEB_RETRY_DELAYS_MS;
        const inFlight = new Map();
        return (chunkId, load)=>{
            const pendingLoad = inFlight.get(chunkId);
            if (pendingLoad != null) return pendingLoad;
            const settle = (retriesDone)=>load().then((loaded)=>{
                    if (retriesDone > 0) {
                        this.report({
                            chunkId,
                            outcome: 'recovered',
                            attempts: retriesDone + 1,
                            error: null
                        });
                    }
                    return loaded;
                }, (error)=>{
                    if (retriesDone < retryDelaysMs.length && !timedOut(error)) {
                        return scheduler.wait(retryDelaysMs[retriesDone]).then(()=>settle(retriesDone + 1));
                    }
                    this.report({
                        chunkId,
                        outcome: 'failed',
                        attempts: retriesDone + 1,
                        error
                    });
                    throw error;
                });
            const attempt = settle(0).finally(()=>inFlight.delete(chunkId));
            inFlight.set(chunkId, attempt);
            return attempt;
        };
    }
    loadRoute(chunkName) {
        return this.readyRoutes.has(chunkName) ? Promise.resolve() : this.ports.routeImporters[chunkName]().then(()=>void this.readyRoutes.add(chunkName));
    }
    whenPrefetchAllowed(prefetch) {
        const { browser } = this.ports;
        if (browser.isAutomated()) return noop;
        if (browser.readPageLoadStatus().visibilityState === 'visible') {
            prefetch();
            return noop;
        }
        const stopWaiting = browser.registerBecameVisibleListener(()=>{
            stopWaiting();
            prefetch();
        });
        return stopWaiting;
    }
    scheduleIdle(param, task) {
        let { idleTimeoutMs, fallbackDelayMs } = param;
        const { scheduler } = this.ports;
        const idleCallback = scheduler.idleCallback();
        if (idleCallback == null) {
            scheduler.setTimeout(task, fallbackDelayMs);
        } else if (idleTimeoutMs == null) {
            idleCallback(task);
        } else {
            idleCallback(()=>task(), {
                timeout: idleTimeoutMs
            });
        }
    }
    // WHY: one chunk per idle slot, never a mount-time burst — importing everything at once parses all routes back-to-back on the main thread (TBT); sequential idle keeps routes warm (no Suspense flicker on navigation) without competing with interaction.
    warmRoutesOnePerIdleSlot() {
        const queue = ROUTE_CHUNK_ENTRIES.map((param)=>{
            let { chunkName } = param;
            return chunkName;
        });
        const next = ()=>{
            const chunkName = queue.shift();
            if (chunkName == null) return;
            void this.loadRoute(chunkName).catch(noop).finally(()=>this.scheduleIdle(ROUTE_IDLE_POLICY, next));
        };
        this.scheduleIdle(ROUTE_IDLE_POLICY, next);
    }
    warmMedia() {
        this.scheduleIdle(MEDIA_IDLE_POLICY, ()=>this.ports.mediaImporters.forEach((load)=>void load().then(noop, noop)));
    }
    report(report) {
        const { browser, logBIEvent } = this.ports;
        const telemetry = buildChunkLoadTelemetry(report, browser.readPageLoadStatus(), browser.isAutomated(), browser.getPathname());
        this.sendWhenOnline(()=>logBIEvent(telemetry));
    }
    sendWhenOnline(send) {
        const { browser } = this.ports;
        if (browser.readPageLoadStatus().online) {
            send();
            return;
        }
        const stopWaiting = browser.registerOnlineListener(()=>{
            stopWaiting();
            send();
        });
    }
    constructor(ports){
        _define_property(this, "ports", void 0);
        _define_property(this, "readyRoutes", void 0);
        this.ports = ports;
        this.readyRoutes = new Set();
    }
}
